/**
 * Collection Dock Component
 *
 * A persistent workspace dock that displays the user's default collection.
 * Supports drag-and-drop for adding items, horizontal scrolling through items,
 * and collapsing to a minimal pill indicator.
 */

import { Component, inject, signal, effect, afterNextRender, HostListener, ElementRef, OnDestroy, ChangeDetectionStrategy } from '@angular/core';

import { Router } from '@angular/router';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { TooltipModule } from 'primeng/tooltip';
import { MenuModule } from 'primeng/menu';
import { ButtonModule } from 'primeng/button';
import { MenuItem, ConfirmationService, MessageService } from 'primeng/api';
import { ConfirmDialogModule } from 'primeng/confirmdialog';
import { raiseConfirm } from '@branduals/dialog-guard';
import { FontAwesomeModule } from '@fortawesome/angular-fontawesome';
import {
  faLayerGroup,
  faChevronLeft,
  faChevronRight,
  faChevronDown,
  faDownload,
  faShareNodes,
  faTrash,
  faArrowUpRightFromSquare,
  faPlus,
  faMinus
} from '@fortawesome/pro-duotone-svg-icons';
import { Subscription } from 'rxjs';

import { CollectionDockService } from './collection-dock.service';
import { CollectionDockItemComponent } from './collection-dock-item.component';
import { CollectionSharePanelService } from '../collection-share-panel/collection-share-panel.service';
import { FilePreviewService } from '../../services/file-preview.service';
import { Collection, CollectionItem } from '../../models/collection.models';
import { LocaleService } from '../../services/locale.service';
import { PortalExposureConfirmService } from '../../services/portal-exposure-confirm.service';
import { MembershipService } from '../../services/membership.service';
import { CollectionsService } from '../../services/collections.service';
import { Item, ItemType } from '../../models/item';
import { buildItemUrl } from '../../utils/url.utils';
import { formatCount } from '../../utils/count.utils';
import { readItemIds } from '../../models/drag-formats';
import { LoadFailedComponent } from '../page-states/load-failed.component';
import { OperationalLabelComponent } from '../operational-label/operational-label.component';
import { LabelDescribedByDirective } from '../operational-label/label-described-by.directive';
import { L10nPluralPipe } from '../../pipes/l10n-plural.pipe';

@Component({
  selector: 'app-collection-dock',
  standalone: true,
  imports: [
    TranslateModule,
    TooltipModule,
    MenuModule,
    ButtonModule,
    ConfirmDialogModule,
    FontAwesomeModule,
    CollectionDockItemComponent,
    LoadFailedComponent,
    OperationalLabelComponent,
    LabelDescribedByDirective,
    L10nPluralPipe
  ],
  templateUrl: './collection-dock.component.html',
  styleUrls: ['./collection-dock.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class CollectionDockComponent implements OnDestroy {
  protected readonly formatCount = formatCount;

  // Services
  dockService = inject(CollectionDockService);
  private router = inject(Router);
  private translateService = inject(TranslateService);
  private confirmationService = inject(ConfirmationService);
  private readonly portalExposureConfirm = inject(PortalExposureConfirmService);
  private messageService = inject(MessageService);
  private sharePanelService = inject(CollectionSharePanelService);
  private filePreviewService = inject(FilePreviewService);
  private localeService = inject(LocaleService);
  private readonly membership = inject(MembershipService);
  /** Share and download are a MEMBER's doors (a collection carries no content role, so its download keys on admission); a monitoring root counts. */
  protected readonly isMember = this.membership.isAdmittedOrRoot;
  protected collectionsService = inject(CollectionsService);
  private elementRef = inject(ElementRef);

  // Icons
  faLayerGroup = faLayerGroup;
  faChevronLeft = faChevronLeft;
  faChevronRight = faChevronRight;
  faChevronDown = faChevronDown;
  faDownload = faDownload;
  // Share renders the app-wide share glyph (faShareNodes) — every share affordance
  // carries the same icon, from tiles to dialogs to this strip.
  faShareNodes = faShareNodes;
  faTrash = faTrash;
  faArrowUpRightFromSquare = faArrowUpRightFromSquare;
  faPlus = faPlus;
  faMinus = faMinus;

  // Local state
  canScrollLeft = signal(false);
  canScrollRight = signal(false);

  // Collection switch menu items
  collectionMenuItems = signal<MenuItem[]>([]);

  private subscriptions = new Subscription();
  private scrollContainer: HTMLElement | null = null;

  constructor() {
    // Wire the scroll observer once the DOM has rendered (replaces a setTimeout(100) hack;
    // afterNextRender is the render-timing primitive — fires after the view exists, browser-only).
    afterNextRender(() => this.setupScrollObserver());

    // Rebuild menu whenever available collections or active collection changes
    effect(() => {
      // Reading these signals registers them as dependencies
      this.dockService.availableCollections();
      this.dockService.activeCollection();
      // Rebuild menu
      this.collectionMenuItems.set(this.buildCollectionMenu());
    });
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
    if (this.scrollContainer) {
      this.scrollContainer.removeEventListener('scroll', this.scrollHandler);
    }
  }

  // ========================================================================
  // Scroll Handling
  // ========================================================================

  private readonly scrollHandler = () => this.checkScrollButtons();

  private setupScrollObserver(): void {
    this.scrollContainer = this.elementRef.nativeElement.querySelector('.dock-items');
    if (this.scrollContainer) {
      this.checkScrollButtons();
      this.scrollContainer.addEventListener('scroll', this.scrollHandler);
    }
  }

  private checkScrollButtons(): void {
    if (!this.scrollContainer) return;

    const { scrollLeft, scrollWidth, clientWidth } = this.scrollContainer;
    this.canScrollLeft.set(scrollLeft > 0);
    this.canScrollRight.set(scrollLeft + clientWidth < scrollWidth - 1);
  }

  scrollLeft(): void {
    if (!this.scrollContainer) return;
    this.scrollContainer.scrollBy({ left: -200, behavior: 'smooth' });
  }

  scrollRight(): void {
    if (!this.scrollContainer) return;
    this.scrollContainer.scrollBy({ left: 200, behavior: 'smooth' });
  }

  // ========================================================================
  // Actions
  // ========================================================================

  toggleCollapsed(): void {
    this.dockService.toggle();
  }

  expandDock(): void {
    this.dockService.expand();
  }

  openInCollections(): void {
    const collection = this.dockService.activeCollection();
    if (!collection) return;
    // Build the canonical detail URL `/collections/{slug}-{urlId}` via the shared helper
    // (was previously `/collections?id={guid}` — collections home had no reader for that param).
    const collectionAsItem = {
      id: collection.id,
      type: ItemType.Collection,
      urlId: collection.urlId,
      title: collection.title
    } as unknown as Item;
    void this.router.navigateByUrl(buildItemUrl(collectionAsItem, this.localeService.slugLocale()));
  }

  downloadCollection(): void {
    const collection = this.dockService.activeCollection();
    if (!collection) return;
    // Whole collection — server resolves all members (the dock's items() is page-capped). The service
    // shows an i18n error toast on failure, so no try/catch is needed here.
    void this.collectionsService.downloadCollection(collection.id, undefined, this.localeService.getActiveContentLanguage());
  }

  shareCollection(): void {
    const collection = this.dockService.activeCollection();
    if (collection) {
      this.sharePanelService.open(collection);
    }
  }

  clearCollection(): void {
    raiseConfirm(this.confirmationService, {
      message: this.translateService.instant('collections.dock.clearConfirm'),
      header: this.translateService.instant('collections.dock.clearTitle'),
      icon: 'pi pi-exclamation-triangle',
      acceptLabel: this.translateService.instant('common.remove'),
      rejectLabel: this.translateService.instant('common.cancel'),
      acceptButtonStyleClass: 'p-button-danger',
      accept: () => {
        this.dockService.clearAll();
      }
    });
  }

  // ========================================================================
  // Collection Switching
  // ========================================================================

  buildCollectionMenu(): MenuItem[] {
    const collections = this.dockService.availableCollections();
    const activeId = this.dockService.activeCollection()?.id;

    // A star marks the default collection and nothing marks the rest (an empty slot of the same width keeps the
    // names in one column) — never a folder, which is Drive's sign; the open collection is the menu's current entry.
    return collections.map(collection => ({
      label: this.localeService.l10nLabel(collection.title, 'collections.untitled'),
      icon: collection.isDefault ? 'pi pi-star-fill' : 'pi pi-fw',
      styleClass: collection.id === activeId ? 'menu-item-current' : undefined,
      command: () => this.switchToCollection(collection)
    }));
  }

  switchToCollection(collection: Collection): void {
    this.dockService.switchCollection(collection);
    // Menu closes automatically via PrimeNG
  }

  // ========================================================================
  // Drag and Drop
  // ========================================================================

  // The browser fires a drop only where the SOURCE's allowed effects meet the TARGET's answer: the
  // grids allow copy and move and the dock answers copy. A hover the dock cannot take (a tree node's
  // text, a campaign card, a locale row, a desktop file, a collection the member cannot add to) is
  // left unclaimed and answered none — the pointer paints not-allowed and the banner stays dark; a
  // desktop file's RELEASE is then caught by the shell's file-drop guard, which names the road instead
  // of letting the tab navigate (the dock's own answer is unchanged — it never claims the file).
  // The typed item-ids format is the whole pre-drop test: a target may read the drag's TYPES during
  // dragenter and dragover, never its data.

  @HostListener('dragenter', ['$event'])
  onDragEnter(event: DragEvent): void {
    if (!this.dockService.acceptsDrag(event.dataTransfer?.types ?? [])) return;
    event.preventDefault();
    this.dockService.handleDragEnter();
  }

  @HostListener('dragleave', ['$event'])
  onDragLeave(event: DragEvent): void {
    // Only trigger if leaving the dock entirely
    const rect = this.elementRef.nativeElement.getBoundingClientRect();
    const x = event.clientX;
    const y = event.clientY;

    if (x < rect.left || x > rect.right || y < rect.top || y > rect.bottom) {
      this.dockService.handleDragLeave();
    }
  }

  @HostListener('dragover', ['$event'])
  onDragOver(event: DragEvent): void {
    if (!event.dataTransfer) return;
    if (!this.dockService.acceptsDrag(event.dataTransfer.types)) {
      event.dataTransfer.dropEffect = 'none';
      return;
    }
    event.preventDefault();
    event.dataTransfer.dropEffect = 'copy';
  }

  @HostListener('drop', ['$event'])
  onDrop(event: DragEvent): void {
    // The drop leg tests what the hover tested: a release the hover refused (an OS file, a foreign
    // drag) is left uncancelled, so it bubbles to the shell's file-drop guard, which speaks for a file —
    // a cancelled drop is "someone's" and would silence it.
    if (!event.dataTransfer || !this.dockService.acceptsDrag(event.dataTransfer.types)) return;
    event.preventDefault();
    this.dockService.handleDragLeave();
    // An accepted hover reaches this drop with the typed format as the payload; an empty or
    // already-present set is refused here, before any round trip. The hover accepted on the TYPE alone
    // (the drag model withholds the ids until the release), so a set the strip already holds is the one
    // refusal the member could not see coming: the invitation lit, so the release says why nothing
    // happened (ruled 2026-09-12). An empty or malformed payload is a foreign drag borrowing the type
    // name, not a member's gesture, and stays silent.
    const ids = readItemIds(event.dataTransfer ?? null);
    if (!this.dockService.canAcceptDrop(ids)) {
      // The dock's own voice (its one home is the service): the same sentence the add corridor speaks
      // through the drop route when the duplicates sat beyond the loaded strip (ruled 2026-09-14).
      if (this.dockService.holdsEvery(ids)) this.dockService.speakAlreadyInCollection();
      return;
    }
    void this.addItemsWithPortalCheck(ids);
  }

  /**
   * Every release ends the drag at the in-app SOURCE with a dragend, whether the drop was accepted,
   * refused or landed elsewhere. The rect-guarded dragleave above never runs for a release inside the
   * dock (the pointer is still within the rect), so this listener is what keeps the banner from
   * sticking after the drop. A refused desktop drag never lit the banner, so it needs no clearing.
   */
  @HostListener('document:dragend')
  onDocumentDragEnd(): void {
    this.dockService.handleDragLeave();
  }

  // ========================================================================
  // Item Actions
  // ========================================================================

  onRemoveItem(itemId: string): void {
    this.dockService.removeItem(itemId);
  }

  /**
   * Adds the dropped items to the dock's collection. When that collection is a portal section the member is
   * asked first, in the words that are true of the section right now (public, not public yet, or could not check).
   * Use this from components that drop items onto the dock.
   */
  async addItemsWithPortalCheck(itemIds: string[]): Promise<boolean> {
    const target = this.dockService.activeCollection();
    if (target && this.dockService.isActiveCollectionPortal()) {
      const confirmed = await this.portalExposureConfirm.confirmItemAddition(this.confirmationService, target.id);
      if (!confirmed) return false;
    }
    return this.dockService.addItems(itemIds);
  }

  onItemDoubleClick(collectionItem: CollectionItem): void {
    const item = collectionItem.item;
    if (!item) return;

    // Don't open preview for folders/galleries - navigate instead
    if (item.type === ItemType.Folder || item.type === ItemType.Gallery) {
      // Navigate to the folder/gallery in appropriate module
      return;
    }

    // Get all previewable items (non-folder/gallery)
    const previewableItems = this.dockService.items()
      .filter(ci => ci.item && ci.item.type !== ItemType.Folder && ci.item.type !== ItemType.Gallery)
      .map(ci => ci.item!);

    // Open file preview
    this.filePreviewService.open({
      item: item,
      items: previewableItems,
      canEdit: true,
      showUnpublished: true
    });
  }

}
